import React from 'react';
import { render } from 'react-dom';
import {  Provider  } from 'react-redux';
import { Router, Switch, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { ruRU } from '@mui/material/locale';

import { Notifier, Confirm, Auth, Reset } from './_Components'

import { history, store } from './_utils';

import './index.css';
import App from './App';
import * as serviceWorker from './serviceWorker';

const ink = '#0B2033';
const accent = '#0089B8';
const accentInk = '#00648A';
const accentSoft = '#E1F1F8';
const canvas = '#EEF1F5';
const surface = '#FFFFFF';
const surface2 = '#F7F9FB';
const line = '#DCE3EA';
const muted = '#63788C';
const danger = '#C8354A';
const ok = '#17875C';
const warn = '#B26A00';
const sans = '"IBM Plex Sans", -apple-system, "Segoe UI", Roboto, sans-serif';
const mono = '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace';

const theme = createTheme({
    breakpoints: {
        values: {
          xs: 0,
          sm: 600,
          md: 900,
          lg: 1200,
          xl: 1536,
        },
    },
    typography: {
        fontFamily: sans,
        fontStyle: 'normal',
        fontSize: 13,
        allVariants: {
            color: ink,
        },
        h1: { fontSize: 19, fontWeight: 600, letterSpacing: '-0.01em' },
        h2: { fontSize: 13.5, fontWeight: 600 },
        body1: { fontSize: 13, lineHeight: 1.45 },
        body2: { fontSize: 12.5, lineHeight: 1.45 },
        caption: { fontSize: 11.5, color: muted },
        button: { textTransform: 'none', fontWeight: 500 },
    },
    palette: {
        primary: {
            light: '#3BA4C9',
            main: accent,
            dark: accentInk,
            contrastText: '#fff',
        },
        secondary: {
            main: danger,
            contrastText: '#fff',
        },
        info: {
            main: accent,
        },
        success: {
            main: ok,
        },
        warning: {
            main: warn,
        },
        error: {
            main: danger,
        },
        text: {
            primary: ink,
            secondary: muted,
        },
        background: {
            default: canvas,
            paper: surface,
        },
        divider: line,
    },
    shape: {
        borderRadius: 6,
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: canvas,
                    color: ink,
                },
            },
        },
        MuiList: {
            styleOverrides: {
                root: {
                    cursor: 'pointer',
                }
            }
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    padding: '0px 0px'
                }
            }
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    minWidth: 28,
                    color: 'inherit',
                }
            }
        },
        MuiListItemButton: {
            styleOverrides: {
              root: {
                color: ink,
                cursor: 'pointer',
                minHeight: 32,
                borderRadius: 6,
                '&.Mui-selected': {
                    color: accentInk,
                    backgroundColor: accentSoft,
                },
                '&:hover, &.Mui-selected:hover': {
                    backgroundColor: accentSoft,
                }
              }
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    minHeight: 30,
                    height: 30,
                    padding: '0 12px',
                    borderRadius: 6,
                    textTransform: 'none',
                    fontSize: 12.5,
                    fontWeight: 500,
                    boxShadow: 'none',
                    minWidth: 0,
                },
                containedPrimary: {
                    fontWeight: 600,
                    '&:hover': {
                        backgroundColor: accentInk,
                    },
                    '&.Mui-disabled': {
                        backgroundColor: line,
                        color: '#96A6B4',
                    },
                },
                outlined: {
                    borderColor: line,
                    color: '#14324D',
                    backgroundColor: surface,
                    '&:hover': {
                        backgroundColor: surface2,
                        borderColor: '#C7D2DC',
                    },
                },
                outlinedPrimary: {
                    borderColor: line,
                    color: '#14324D',
                    backgroundColor: surface,
                    '&:hover': {
                        backgroundColor: surface2,
                        borderColor: '#C7D2DC',
                    },
                },
                outlinedSecondary: {
                    color: danger,
                    borderColor: '#EFD3D8',
                    '&:hover': {
                        backgroundColor: '#FBE9EC',
                        borderColor: '#E4BCC3',
                    },
                },
                sizeSmall: {
                    height: 26,
                    minHeight: 26,
                    padding: '0 9px',
                    fontSize: 11.5,
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    borderRadius: 6,
                },
                sizeSmall: {
                    width: 28,
                    height: 28,
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    marginRight: 0
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    backgroundColor: surface,
                    borderRadius: 6,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#C7D2DC',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: accent,
                        borderWidth: 1,
                    },
                    '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${accentSoft}`,
                    },
                },
                input: {
                    padding: '7px 10px',
                    fontSize: 13,
                    height: 'auto',
                },
                notchedOutline: {
                    borderColor: line,
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontSize: 12.5,
                    color: muted,
                    '&.Mui-focused': {
                        color: accentInk,
                    },
                },
            },
        },
        MuiSelect:{
            styleOverrides: {
                select: {
                    height: 'auto',
                    minHeight: '1.25em',
                }
            }
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    backgroundColor: surface2,
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    padding: '0 12px',
                    height: 34,
                    fontSize: 12.5,
                    color: ink,
                    borderBottom: '1px solid #EAEFF3',
                    verticalAlign: 'middle',
                },
                head: {
                    backgroundColor: surface2,
                    color: muted,
                    fontFamily: mono,
                    fontSize: 9.5,
                    fontWeight: 500,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    height: 32,
                    whiteSpace: 'nowrap',
                    borderBottom: `1px solid ${line}`,
                }
            }
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:hover': {
                        backgroundColor: surface2,
                    },
                },
            },
        },
        MuiTextField: {
            defaultProps: {
                size: 'small',
                variant: 'outlined',
            },
            styleOverrides: {
                root: {
                    minWidth: 0,
                }
            }
        },
        MuiSvgIcon: {
            styleOverrides: {
                root: {
                    fontSize: '1.1rem'
                }
            }
        },
        MuiInputAdornment: {
            styleOverrides: {
                root: {
                    color: muted
                }
            }
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    border: `1px solid ${line}`,
                    boxShadow: '0 1px 2px rgba(11,32,51,.06), 0 1px 8px rgba(11,32,51,.04)',
                }
            }
        },
        MuiCardHeader: {
            styleOverrides: {
                root: {
                    padding: '11px 14px',
                    borderBottom: '1px solid #EAEFF3',
                },
                title: {
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: ink,
                },
                action: {
                    margin: 0,
                    alignSelf: 'center',
                },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    padding: 14,
                    '&:last-child': {
                        paddingBottom: 14,
                    },
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                label: {
                    color: ink,
                    fontSize: 12.5
                }
            }
        },
        MuiInputBase: {
            styleOverrides: {
                input: {
                    fontSize: 13
                }
            }
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    fontSize: 12.5,
                    fontWeight: 500,
                    minHeight: 34,
                    minWidth: 0,
                    padding: '0 13px',
                    color: muted,
                    textTransform: 'none',
                    '&.Mui-selected': {
                        color: accentInk,
                        fontWeight: 600,
                    },
                }
            }
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    minHeight: 34,
                },
                indicator: {
                    height: 2,
                    backgroundColor: accent,
                },
            }
        },
        MuiToolbar: {
            styleOverrides: {
                root: {
                    minHeight: 36
                }
            }
        },
        MuiAutocomplete: {
            styleOverrides: {
                root: {
                    minWidth: 0,
                },
                option: {
                    fontSize: 12.5,
                }
            }
        },
        MuiTypography: {
            styleOverrides: {
                root: {
                    color: ink
                }
            }
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    height: 19,
                    borderRadius: 4,
                    fontSize: 11,
                    fontWeight: 500,
                },
                label: {
                    padding: '0 7px',
                    fontWeight: 500,
                }
            }
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    fontSize: 12.5
                }
            }
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: {
                    minHeight: 24
                },
                content: {
                    padding: 0,
                    margin: 0
                }
            }
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    padding: 4
                }
            }
        },
        MuiPaper: {
            styleOverrides: {
                rounded: {
                    borderRadius: 8,
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    fontSize: 16,
                    fontWeight: 600,
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    paddingTop: '20px !important',
                },
            },
        },
    }
}, ruRU);

render(
    <ThemeProvider theme={theme}>
        <Provider store={store}>
            <Router history={history}>
                <Switch>
                    <Route exact path='/login' component={Auth}/>
                    <Route exact path='/reset' component={Reset}/>
                    <Route path='/' component={App}/>
                </Switch>
            </Router>
            <Notifier />
            <Confirm />
        </Provider>
    </ThemeProvider>,
    document.getElementById('root')
);

serviceWorker.unregister();
